export type ChainKind = 'evm' | 'svm'

export type Chain = {
  domain: number
  name: string
  short: string
  cctp: 'v1' | 'v2' | 'both'
  accent: string
  kind?: ChainKind
  rpc?: string
  txUrl?: string
  addrUrl?: string
  messageTransmitterV1?: string
  messageTransmitterV2?: string
  blockSec?: number
}

// MessageTransmitterV2 is deployed at the same CREATE2 address on every v2 chain.
const MT_V2 = '0x81D40F21F12A8F0E3252Bccb954D722d4c464B64'

const evm = (base: string, rpc?: string): Pick<Chain, 'kind' | 'rpc' | 'txUrl' | 'addrUrl'> => ({
  kind: 'evm',
  rpc,
  txUrl: `${base}/tx/`,
  addrUrl: `${base}/address/`,
})

export const CHAINS: Record<number, Chain> = {
  0: {
    domain: 0,
    name: 'Ethereum',
    short: 'ETH',
    cctp: 'both',
    accent: '#6679d6',
    ...evm('https://etherscan.io', 'https://ethereum-rpc.publicnode.com'),
    messageTransmitterV1: '0x0a992d191DEeC32aFe36203Ad87D7d289a738F81',
    messageTransmitterV2: MT_V2,
    blockSec: 12,
  },
  1: {
    domain: 1,
    name: 'Avalanche',
    short: 'AVAX',
    cctp: 'both',
    accent: '#e84142',
    ...evm('https://snowtrace.io', 'https://avalanche-c-chain-rpc.publicnode.com'),
    messageTransmitterV1: '0x8186359aF5F57FbB40c6b14A588d2A59C0C29880',
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  2: {
    domain: 2,
    name: 'OP Mainnet',
    short: 'OP',
    cctp: 'both',
    accent: '#f23344',
    ...evm('https://optimistic.etherscan.io', 'https://optimism-rpc.publicnode.com'),
    messageTransmitterV1: '0x4D41f22c5a0e5c74090899E5a8Fb597a8842b3e8',
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  3: {
    domain: 3,
    name: 'Arbitrum',
    short: 'ARB',
    cctp: 'both',
    accent: '#28a0f0',
    ...evm('https://arbiscan.io', 'https://arb1.arbitrum.io/rpc'),
    messageTransmitterV1: '0xC30362313FBBA5cf9163F0bb16a0e01f01A896ca',
    messageTransmitterV2: MT_V2,
    blockSec: 0.25,
  },
  4: { domain: 4, name: 'Noble', short: 'NOBLE', cctp: 'v1', accent: '#a78bfa' },
  5: {
    domain: 5,
    name: 'Solana',
    short: 'SOL',
    cctp: 'both',
    accent: '#9b6dff',
    kind: 'svm',
    rpc: 'https://solana-rpc.publicnode.com',
    txUrl: 'https://solscan.io/tx/',
    addrUrl: 'https://solscan.io/account/',
  },
  6: {
    domain: 6,
    name: 'Base',
    short: 'BASE',
    cctp: 'both',
    accent: '#2154ff',
    ...evm('https://basescan.org', 'https://base-rpc.publicnode.com'),
    messageTransmitterV1: '0xAD09780d193884d503182aD4588450C416D6F9D4',
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  7: {
    domain: 7,
    name: 'Polygon PoS',
    short: 'POL',
    cctp: 'both',
    accent: '#8247e5',
    ...evm('https://polygonscan.com', 'https://polygon-bor-rpc.publicnode.com'),
    messageTransmitterV1: '0xF3be9355363857F3e001be68856A2f96b4C39Ba9',
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  8: { domain: 8, name: 'Sui', short: 'SUI', cctp: 'v1', accent: '#4ca2ff' },
  9: { domain: 9, name: 'Aptos', short: 'APT', cctp: 'v1', accent: '#1abc9c' },
  10: {
    domain: 10,
    name: 'Unichain',
    short: 'UNI',
    cctp: 'v2',
    accent: '#ff007a',
    ...evm('https://uniscan.xyz', 'https://unichain-rpc.publicnode.com'),
    messageTransmitterV1: '0x353bE9E2E38AB1D19104534e4edC21c643Df86f4',
    messageTransmitterV2: MT_V2,
    blockSec: 1,
  },
  11: {
    domain: 11,
    name: 'Linea',
    short: 'LINEA',
    cctp: 'v2',
    accent: '#d4a017',
    ...evm('https://lineascan.build', 'https://linea-rpc.publicnode.com'),
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  12: {
    domain: 12,
    name: 'Codex',
    short: 'CDX',
    cctp: 'v2',
    accent: '#00b89c',
    kind: 'evm',
    rpc: 'https://rpc.codex.xyz',
  },
  13: {
    domain: 13,
    name: 'Sonic',
    short: 'SONIC',
    cctp: 'v2',
    accent: '#ff7a1c',
    ...evm('https://sonicscan.org', 'https://rpc.soniclabs.com'),
    messageTransmitterV2: MT_V2,
    blockSec: 1,
  },
  14: {
    domain: 14,
    name: 'World Chain',
    short: 'WORLD',
    cctp: 'v2',
    accent: '#3a6cd9',
    ...evm('https://worldscan.org', 'https://worldchain-mainnet.g.alchemy.com/public'),
    messageTransmitterV2: MT_V2,
    blockSec: 2,
  },
  15: {
    domain: 15,
    name: 'Monad',
    short: 'MON',
    cctp: 'v2',
    accent: '#836ef9',
    kind: 'evm',
    rpc: 'https://rpc3.monad.xyz',
  },
  16: {
    domain: 16,
    name: 'Sei',
    short: 'SEI',
    cctp: 'v2',
    accent: '#c33d4e',
    kind: 'evm',
    rpc: 'https://evm-rpc.sei-apis.com',
    messageTransmitterV2: MT_V2,
    blockSec: 0.4,
  },
  17: {
    domain: 17,
    name: 'BNB Smart Chain',
    short: 'BNB',
    cctp: 'v2',
    accent: '#c69300',
    ...evm('https://bscscan.com', 'https://bsc-rpc.publicnode.com'),
  },
  18: {
    domain: 18,
    name: 'XDC',
    short: 'XDC',
    cctp: 'v2',
    accent: '#2a71d0',
    kind: 'evm',
    rpc: 'https://rpc.xdc.org',
  },
  19: {
    domain: 19,
    name: 'HyperEVM',
    short: 'HYPE',
    cctp: 'v2',
    accent: '#00b386',
    kind: 'evm',
    rpc: 'https://rpc.hyperliquid.xyz/evm',
  },
  21: {
    domain: 21,
    name: 'Ink',
    short: 'INK',
    cctp: 'v2',
    accent: '#6f4cf5',
    kind: 'evm',
    rpc: 'https://rpc-gel.inkonchain.com',
    messageTransmitterV2: MT_V2,
    blockSec: 1,
  },
  22: {
    domain: 22,
    name: 'Plume',
    short: 'PLUME',
    cctp: 'v2',
    accent: '#ec4899',
    kind: 'evm',
    rpc: 'https://rpc.plume.org',
    messageTransmitterV2: MT_V2,
    blockSec: 1,
  },
  25: { domain: 25, name: 'Starknet', short: 'STRK', cctp: 'v2', accent: '#f64f3b' },
  28: { domain: 28, name: 'EDGE', short: 'EDGE', cctp: 'v2', accent: '#0d9488' },
  30: {
    domain: 30,
    name: 'Morph',
    short: 'MORPH',
    cctp: 'v2',
    accent: '#2dba65',
    kind: 'evm',
    rpc: 'https://rpc.morphl2.io',
  },
  31: { domain: 31, name: 'Pharos', short: 'PHA', cctp: 'v2', accent: '#3b82f6' },
}

export function chainOf(domain: number | string | undefined): Chain | null {
  if (domain == null || domain === '') return null
  const d = typeof domain === 'string' ? Number(domain) : domain
  return CHAINS[d] ?? null
}

export function explorerTxUrl(
  chain: Chain | null | undefined,
  hash: string | undefined,
): string | undefined {
  if (!chain?.txUrl || !hash) return undefined
  return chain.txUrl + hash
}
